import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, Calendar } from "lucide-react";
import { fetchBriefList } from "@/lib/api";
import NewsletterForm from "@/components/NewsletterForm";
import useSEO from "@/lib/useSEO";

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); } catch { return ""; }
};

export default function BriefArchive() {
  useSEO({
    title: "The Brief: Weekly Creator Legal Digest | Upload Counsel",
    description: "Every Friday, we send a plain-English digest of the week's creator-economy legal news. Read past editions here.",
    canonical: "https://uploadlawyer.com/brief",
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchBriefList();
        setItems(data.items || []);
      } catch (e) {
        console.error("Failed to load briefs:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="page-brief-archive">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 glow-tl" />
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 pt-24 pb-12 md:pt-32">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D9B36C]/40 bg-[#D9B36C]/10 text-[#D9B36C] text-xs font-semibold uppercase tracking-widest">
            <Mail size={12} /> The Brief · Weekly Newsletter
          </div>
          <h1 className="mt-6 font-display text-5xl sm:text-6xl font-extrabold text-white tracking-tight">
            The Brief.
          </h1>
          <p className="mt-4 text-slate-300 text-lg max-w-2xl leading-relaxed">
            Every Friday. The week's creator-economy legal news, curated with an Upload Counsel "why it matters" angle. Plain English. No fluff. One short read.
          </p>
          <div className="mt-8 max-w-md">
            <NewsletterForm source="brief-archive" />
          </div>
        </div>
      </section>

      <section className="relative py-16 border-t border-[#272B36]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">Past editions</p>
          <h2 className="mt-3 font-display text-3xl font-bold text-white tracking-tight">Archive</h2>
          <div className="mt-8 space-y-4" data-testid="brief-list">
            {loading && <p className="text-slate-500">Loading…</p>}
            {!loading && items.length === 0 && (
              <p className="text-slate-500">No editions published yet. The first one drops this Friday.</p>
            )}
            {items.map((b) => (
              <Link
                key={b.id}
                to={`/brief/${b.id}`}
                data-testid={`brief-card-${b.id}`}
                className="beam-card group block rounded-2xl border border-[#272B36] bg-[#121419] p-6 hover:border-[#D9B36C]/50 transition-colors"
              >
                <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-widest">
                  <Calendar size={12} /> {formatDate(b.sent_at)}
                </div>
                <h3 className="mt-2 font-display text-xl font-semibold text-white group-hover:text-[#D9B36C] transition-colors">{b.subject}</h3>
                {b.intro && <p className="mt-2 text-slate-400 text-sm leading-relaxed line-clamp-2">{b.intro}</p>}
                <p className="mt-3 text-xs text-slate-500">{(b.items || []).length} stories curated</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-[#D9B36C] font-medium group-hover:gap-2 transition-all">Read edition <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
